import React from 'react';
import { getApiBaseUrl } from '../../../utils/apiConfig';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const TIPOS = ['Fone1', 'WhatsApp', 'Fone2'];

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  };
}

function digits(value) {
  return String(value || '').replace(/\D/g, '');
}

function maskPhone(value) {
  const n = digits(value).slice(0, 11);
  if (!n.length) return '';
  if (n.length <= 2) return `(${n}`;
  if (n.length <= 6) return `(${n.slice(0, 2)})${n.slice(2)}`;
  if (n.length <= 10) return `(${n.slice(0, 2)})${n.slice(2, 6)}-${n.slice(6)}`;
  return `(${n.slice(0, 2)})${n.slice(2, 7)}-${n.slice(7)}`;
}

function phoneKey(tipo) {
  if (tipo === 'WhatsApp') return 'whats';
  if (tipo === 'Fone2') return 'fone2';
  return 'fone1';
}

export function useOrderDetailsSolicitanteEdit(order, { onSolicitanteUpdated, setSolicitanteEmail } = {}) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [feedback, setFeedback] = React.useState(null);
  const [savedNote, setSavedNote] = React.useState(null);
  const [nome, setNome] = React.useState('');
  const [tipo, setTipo] = React.useState('Fone1');
  const [fone1, setFone1] = React.useState('');
  const [whats, setWhats] = React.useState('');
  const [fone2, setFone2] = React.useState('');
  const [emails, setEmails] = React.useState([]);
  const [emailDraft, setEmailDraft] = React.useState('');

  const phones = { fone1, whats, fone2 };

  React.useEffect(() => {
    setOpen(false);
    setFeedback(null);
    setSavedNote(null);
  }, [order?.id]);

  const openModal = React.useCallback(async () => {
    if (!order?.id) return;
    setOpen(true);
    setLoading(true);
    setFeedback(null);
    setSavedNote(null);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/orders/${encodeURIComponent(order.id)}/solicitante`, {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) throw new Error(data?.message || 'Não foi possível carregar o solicitante');
      const row = data.data || {};
      const f1 = maskPhone(row.fone);
      const wa = maskPhone(row.celular);
      const f2 = maskPhone(row.fone2);
      setNome(String(row.solicitante || ''));
      setFone1(f1);
      setWhats(wa);
      setFone2(f2);
      setTipo(digits(f1).length >= 10 ? 'Fone1' : digits(wa).length >= 10 ? 'WhatsApp' : digits(f2).length >= 10 ? 'Fone2' : 'Fone1');
      setEmails(String(row.email || '').split(';').map((s) => s.trim().toLowerCase()).filter(Boolean));
      setEmailDraft('');
    } catch (err) {
      setFeedback({ type: 'error', message: err.message || 'Não foi possível carregar o solicitante' });
    } finally {
      setLoading(false);
    }
  }, [order?.id]);

  const closeModal = React.useCallback(() => {
    if (saving) return;
    setOpen(false);
    setFeedback(null);
  }, [saving]);

  const setPhone = React.useCallback((value) => {
    const masked = maskPhone(value);
    if (tipo === 'WhatsApp') setWhats(masked);
    else if (tipo === 'Fone2') setFone2(masked);
    else setFone1(masked);
  }, [tipo]);

  const applyEmail = React.useCallback((text) => {
    const raw = String(text || '');
    if (!/[\s,;]/.test(raw)) {
      setEmailDraft(raw.slice(0, 200));
      return;
    }
    const bits = raw.split(/[\s,;]+/);
    const trailing = /[\s,;]$/.test(raw);
    let draft = trailing ? '' : (bits.pop() || '');
    setEmails((prev) => {
      const next = prev.slice();
      bits.forEach((bit) => {
        const addr = bit.trim().toLowerCase();
        if (EMAIL_RE.test(addr) && next.indexOf(addr) < 0 && next.concat([addr]).join(';').length <= 200) next.push(addr);
      });
      return next;
    });
    setEmailDraft(draft.slice(0, 200));
  }, []);

  const commitEmail = React.useCallback(() => {
    const addr = String(emailDraft || '').trim().toLowerCase();
    if (!addr || !EMAIL_RE.test(addr)) return;
    setEmails((prev) => (prev.indexOf(addr) >= 0 || prev.concat([addr]).join(';').length > 200 ? prev : prev.concat([addr])));
    setEmailDraft('');
  }, [emailDraft]);

  const removeEmail = React.useCallback((addr) => {
    setEmails((prev) => prev.filter((item) => item !== addr));
  }, []);

  const nomeOk = nome.trim().length >= 3;
  const contatoOk = [fone1, whats, fone2].some((p) => digits(p).length >= 10);
  const partialPhone = [fone1, whats, fone2].some((p) => {
    const n = digits(p).length;
    return n > 0 && n < 10;
  });
  const draftOk = !String(emailDraft || '').trim() || EMAIL_RE.test(String(emailDraft).trim());
  const canConfirm = nomeOk && contatoOk && !partialPhone && draftOk && !loading && !saving;

  const confirm = React.useCallback(async () => {
    if (!order?.id || !canConfirm) return;
    const draft = String(emailDraft || '').trim().toLowerCase();
    const list = emails.slice();
    if (draft && EMAIL_RE.test(draft) && list.indexOf(draft) < 0) list.push(draft);
    setSaving(true);
    setFeedback(null);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/orders/${encodeURIComponent(order.id)}/solicitante`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          solicitante: nome.trim(),
          fone: digits(fone1),
          fone2: digits(fone2),
          celular: digits(whats),
          email: list.join(';'),
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) throw new Error(data?.message || 'Erro ao atualizar solicitante');
      const saved = data.data || {};
      setSolicitanteEmail?.(saved.email ? String(saved.email) : null);
      onSolicitanteUpdated?.(saved);
      setOpen(false);
      setSavedNote('Solicitante atualizado.');
      window.setTimeout(() => setSavedNote(null), 2500);
    } catch (err) {
      setFeedback({ type: 'error', message: err.message || 'Erro ao atualizar solicitante' });
    } finally {
      setSaving(false);
    }
  }, [order?.id, canConfirm, emailDraft, emails, nome, fone1, fone2, whats, setSolicitanteEmail, onSolicitanteUpdated]);

  return {
    open,
    loading,
    saving,
    feedback,
    savedNote,
    nome,
    setNome,
    tipo,
    setTipo,
    phone: phones[phoneKey(tipo)] || '',
    setPhone,
    phones,
    emails,
    emailDraft,
    applyEmail,
    commitEmail,
    removeEmail,
    nomeOk,
    contatoOk,
    draftOk,
    canConfirm,
    tipos: TIPOS,
    openModal,
    closeModal,
    confirm,
  };
}
